import React, { useEffect } from 'react';
import {
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Stack,
} from '@mui/material';
import { theme } from '../../theme';
import { Uom } from '../../app/services/api/generated';

interface PropsInterface {
  fullWidth?: boolean;
  quantity: number | string | undefined;
  onQuantityChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onQuantityBlur?: (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  seletedUom: string | null;
  disabled?: boolean;
  disabledUom?: boolean;
  onUomChange?: (event: SelectChangeEvent<string>) => void;
  uoms?: Uom[];
  error?: boolean;
  helperText?: string;
  id?: string;
  allowPrimaryUomDecimal?: boolean;
  primaryUom?: string;
  hideZeroQuantityOnPrint?: boolean;
  formatDecimalOnLoad?: boolean;
}

export const DynamicUomSelect = (props: PropsInterface) => {

  const isFullWidth = props.fullWidth ?? false;
  const isDisabled = props.disabled ?? false;
  const isDisabledUom = props.disabledUom ?? false;
  const isAllowPrimaryUomDecimal = props.allowPrimaryUomDecimal ?? false;
  const isHideZeroQuantityOnPrint = props.hideZeroQuantityOnPrint ?? false;

  const isAllowDecimal = (isAllowPrimaryUomDecimal && props.uoms?.some(x => { return x.code == props.seletedUom && x.toPrimaryConversion == 1; })) ?? false;

  const uomDisabled = props.disabled || !props.onUomChange || isDisabledUom || (props.uoms && props.uoms.length < 2 && props.seletedUom);
  const uomDisabledVissual = props.disabled || !props.onUomChange || isDisabledUom;

  useEffect(() => {
    (props.formatDecimalOnLoad ?? true) === true &&
      quantityToCorrectDecimal(props.quantity?.toString() ?? '', props.seletedUom ?? '');
  }, []);

  useEffect(() => {
    (props.formatDecimalOnLoad ?? true) === true &&
      quantityToCorrectDecimal(props.quantity?.toString() ?? '', props.seletedUom ?? '');
  }, [props.uoms]);

  const handleQuantityChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!props.onQuantityChange) return;

    const newValue = event.target.value;

    if (isAllowDecimal) {
      const parsedValue = Number(newValue);
      if (!isNaN(parsedValue) || newValue === '-') {
        // Allow up to 2 decimal places
        const numericValue = newValue.replace(/[^0-9.]/g, ''); // Remove invalid characters
        const formattedValue = numericValue.split('.').slice(0, 2).join('.'); // Ensure only one decimal point

        const decimalRegex = /^(\d+)(\.\d{0,2})?/; // Match up to 2 decimal places
        const decimalMatch = decimalRegex.exec(formattedValue); // Use exec() instead of match()
        if (decimalMatch) {
          let cleaned = decimalMatch[0];

          // Remove leading zeros from the integer part
          cleaned = cleaned.replace(/^0+(\d)/, '$1');

          event.target.value = cleaned;
          props.onQuantityChange(event);
        }
        else {
          event.target.value = '';
          props.onQuantityChange(event);
        }
      }
      else if (newValue === '') {
        event.target.value = '';
        props.onQuantityChange(event);
      }
    }
    else {
      const integerPart = newValue.split('.')[0].replace(/\D/g, ''); // Take only numbers before '.'
      const parsedValue = Number(integerPart);
      if (!isNaN(parsedValue)) {
        event.target.value = parsedValue.toString(); // Update the value to ensure it's an integer
        props.onQuantityChange(event);
      }
    }
  };

  const quantityToCorrectDecimal = (newValue: string, newUom?: string) => {
    let localIsAllowDecimal = false;
    if (newUom) {
      localIsAllowDecimal = (isAllowPrimaryUomDecimal && props.uoms?.some(x => { return x.code == newUom && x.toPrimaryConversion == 1; })) ?? false;
    } else {
      localIsAllowDecimal = isAllowDecimal;
    }
    let finalValue = '';

    const numericValue = newValue.replace(/[^0-9.]/g, ''); // Remove invalid characters
    if (numericValue == '') return '';

    const formattedValue = numericValue.split('.').slice(0, 2).join('.'); // Ensure a single decimal point
    const [integerPart, decimalPart = ''] = formattedValue.split('.');
    if (localIsAllowDecimal) {
      // Process the input value for decimals
      finalValue = decimalPart.length > 0
        ? `${integerPart}.${decimalPart.padEnd(2, '0').slice(0, 2)}`
        : `${integerPart}.00`; // Ensure 2 decimal places
    }
    else {
      // Process the input value for decimals
      finalValue = integerPart || '0'; // Default to '0' if no valid numeric part
    }
    // Create a new synthetic event
    const syntheticEvent: React.ChangeEvent<HTMLInputElement> = {
      target: {
        value: finalValue, // Corrected value
      } as HTMLInputElement,
      currentTarget: {} as HTMLInputElement, // Optional
      bubbles: true,
      cancelable: true,
      isDefaultPrevented: (): boolean => { return true; },
      isPropagationStopped: (): boolean => { return true; },
      timeStamp: 0,
      defaultPrevented: true,
      eventPhase: 0,
      isTrusted: true,
      nativeEvent: {} as Event,
      preventDefault: () => { },
      stopPropagation: () => { },
      persist: () => { }, // For React's synthetic event system
      type: 'change',
    };

    props.onQuantityChange && props.onQuantityChange(syntheticEvent);
  };

  const handleQuantityBlur = (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const newValue = event.target.value;

    quantityToCorrectDecimal(newValue);

    if (!props.onQuantityBlur) return;

    if (isAllowDecimal) {
      // Allow decimals if `isAllowDecimal` is true
      const parsedValue = parseFloat(newValue);
      if (!isNaN(parsedValue) || newValue === '-') {
        props.onQuantityBlur(event);
      }
    }
    else {
      const integerPart = newValue.split('.')[0].replace(/\D/g, ''); // Take only numbers before '.'
      const parsedValue = Number(integerPart);
      if (!isNaN(parsedValue)) {
        event.target.value = parsedValue.toString(); // Update the value to ensure it's an integer
        props.onQuantityBlur(event);
      }
    }
  };

  const handleSelectChange = (event: SelectChangeEvent<string>) => {
    props.onUomChange && props.onUomChange(event);
  };

  const handleSelectBlur = (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement, Element>) => {
    console.log('handleSelectBlur');
    const quant = props.quantity ?? '';
    quantityToCorrectDecimal(quant.toString(), event.target.value);
  };

  const label = () => {
    let primaryToUse = '';
    if (props.primaryUom) {
      primaryToUse = props.primaryUom;
    }
    else {
      primaryToUse = props.uoms?.find((x) => x.toPrimaryConversion === 1)?.code ?? '';
    }

    if (primaryToUse == '') return '';

    const conversion = props.uoms?.find((x) => x.code === props.seletedUom)?.toPrimaryConversion;

    if (!conversion) return '';

    if (primaryToUse == props.seletedUom) return '';

    return `${conversion} ${primaryToUse}`;
  };


  return (
    <Stack direction="row"
      sx={{ alignItems: 'flex-start', width: '100%' }}>
      <TextField
        inputProps={{
          pattern: '[0-9]*',
          className:
            isHideZeroQuantityOnPrint && (props.quantity === 0 || props.quantity === '0')
              ? 'hide-text-on-print'
              : undefined,
        }}
        id={`${props.id}-quantity`}
        error={props.error}
        value={props.quantity ?? ''}
        onChange={handleQuantityChange}
        onBlur={handleQuantityBlur}
        onClick={(event) => {
          if (isDisabled) {
            event.preventDefault(); // Prevent dropdown from opening
          }
        }}
        helperText={props.helperText}
        sx={{
          pointerEvents: isDisabled ? 'none' : 'auto',
          backgroundColor: isDisabled ? theme.palette.custom.gray[100] : theme.palette.common.white,
          flexGrow: isFullWidth ? 1 : 'unset',
          width: isFullWidth ? '100%' : '75px',
          minWidth: '75px',
          '& .MuiOutlinedInput-root': {
            borderTopRightRadius: 0,
            borderBottomRightRadius: 0,
          },
          '& .MuiFormHelperText-root': {
            marginTop: 0,
          },
        }}
      />
      <FormControl
        sx={{
          flexShrink: 0,
          width: '80px',
          '& .MuiOutlinedInput-root': {
            borderTopLeftRadius: 0,
            borderBottomLeftRadius: 0,
            height: '100%',
          },
        }}
      >
        <InputLabel sx={{ fontWeight: 'bold' }}>
          {label()}
        </InputLabel>
        <Select
          error={props.error}
          label={label()}
          value={props.seletedUom ?? ''}
          onChange={handleSelectChange}
          onBlur={handleSelectBlur}
          IconComponent={uomDisabled ? () => null : undefined}
          onClick={(event) => {
            if (uomDisabled) {
              event.preventDefault(); // Prevent dropdown from opening
            }
          }}
          sx={{
            pointerEvents: uomDisabled ? 'none' : 'auto', // Prevent interaction if 1 option
            backgroundColor: uomDisabledVissual ? theme.palette.custom.gray[100] : theme.palette.common.white,
          }}
        >
          {props.uoms ? props.uoms?.map((uom) => (
            <MenuItem key={uom.code}
              value={uom.code}>
              {uom.code}
            </MenuItem>
          ))
            :
            <MenuItem key={props.seletedUom}
              value={props.seletedUom ?? ''}>
              {props.seletedUom}
            </MenuItem>}
        </Select>
      </FormControl>
    </Stack>

  );
};
